import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useSearchParam } from 'react-use';

import { selectUserProject } from 'core/userContext';

import { PROJECT_ID_SEARCH_PARAM_KEY } from '../../constants';

import { useFetchProjectForCurrentSessionCookie } from './useFetchProjectForCurrentSessionCookie';
import { useFetchSessionCookieForOtherProject } from './useFetchSessionCookieForOtherProject';
import { useCanUserAccessProject } from './useCanUserAccessProject';

const removeProjectIdFromUrl = () => {
  const urlWithProjectId = new URL(window.location.href);
  urlWithProjectId.searchParams.delete(PROJECT_ID_SEARCH_PARAM_KEY);
  window.history.replaceState({}, '', urlWithProjectId.toString());
};

/**
 * React hook that returns an initializer function for the user's current project. Invoking
 * this initializer function will ensure that the project ID in both the URL search params
 * and the user's backend session cookie (JWT) match, and that the project corresponding to that
 * ID is loaded into the Redux store. This process is asynchronous, so this hook also
 * returns the state of whether or not the current project has been initialized.
 *
 * Example:
 *
 * ```jsx
 * const MyApp = () => {
 *   const [isInitialized, initialize] = useInitializeCurrentProject();
 *   useEffect(initialize, []);
 *   return isInitialized ? <MyPage /> : <Loader />;
 * };
 * ```
 */
export const useInitializeCurrentProject = (): [isInitialized: boolean, initialize: () => void] => {
  // Get the current project ID (both according to the URL and according to the backend)
  const currentProjectIdStringInUrl = useSearchParam('projectId');
  const isProjectIdSetInUrl = currentProjectIdStringInUrl !== null;
  const currentProjectIdInUrl = parseInt(currentProjectIdStringInUrl || '', 10);
  const currentProjectIdInSessionCookie = useSelector(selectUserProject)?.id;

  // Set up external-facing state
  const [isInitialized, setIsInitialized] = useState(false);

  // Get Redux-bound callbacks for fetching/switching projects
  const fetchProjectForCurrentSessionCookie = useFetchProjectForCurrentSessionCookie();
  const fetchSessionCookieForOtherProject = useFetchSessionCookieForOtherProject();
  const canUserAccessProject = useCanUserAccessProject();

  /**
   * State machine to handle changes to initialization state
   */
  useEffect(() => {
    /** Return early... */
    if (
      /** [STATE 1] ...if already initialized... */
      isInitialized ||
      /** [STATE 2] ...or if haven't started and/or finished loading a current project yet. */
      currentProjectIdInSessionCookie === undefined
    ) {
      return;
    }

    /**
     * Clear the `projectId` search param value and return early...
     */
    if (
      isProjectIdSetInUrl &&
      /** [STATE 3] ...if the search param `projectId` is malformed... */
      (Number.isNaN(currentProjectIdInUrl) ||
        /** [STATE 4] ...or if the user doesn't have access to project whose ID is in `projectId` search param. */
        !canUserAccessProject(currentProjectIdInUrl))
    ) {
      removeProjectIdFromUrl();
      return;
    }

    /**
     * Request a new session cookie/JWT associated the project ID specified in the URL search param
     * and then refetch the current project with the new cookie...
     */
    if (
      /** [STATE 5] ...if `projectId` search param is present and doesn't match the project ID associated with the current session cookie/JWT. */
      isProjectIdSetInUrl &&
      currentProjectIdInUrl !== currentProjectIdInSessionCookie
    ) {
      fetchSessionCookieForOtherProject(currentProjectIdInUrl).then(
        fetchProjectForCurrentSessionCookie,
      );
      return;
    }

    /**
     * [STATE 6] Finish initialization once project IDs in session cookie and URL search params match
     */
    setIsInitialized(true);
  }, [isInitialized, currentProjectIdInSessionCookie, currentProjectIdInUrl, isProjectIdSetInUrl]);

  return [isInitialized, fetchProjectForCurrentSessionCookie];
};
